import { Capacitor } from "@capacitor/core";
import { isPicture } from "@msys/common";
import * as Sentry from "@sentry/react";
import { orientation as exifrOrientation } from "exifr";
import { enqueueSnackbar } from "notistack";
import uploadcare from "uploadcare-widget";
import { getRuntimeConfig } from "../../../runtimeConfig.js";
import { IMAGE_MAX_WIDTH, IMAGE_QUALITY } from "../../constants.js";
import { Attachment } from "@msys/ui";
export type { Attachment };

// Path on the app's own origin that forwards to https://upload.uploadcare.com
// (CloudFront behaviour on the frontend distribution, mirrored by the Vite
// dev-server proxy). Keeps uploads same-origin so corporate proxies that block
// third-party domains don't break them (MEIS-13561).
const UPLOADCARE_PROXY_PATH = "/uc-upload";

// Uploadcare's direct upload (`/base/`) accepts files up to 100 MB. Files at or
// above `multipartMinSize` would be PUT straight to *.s3.amazonaws.com,
// bypassing the proxy, so the threshold sits above that limit.
const UPLOADCARE_DIRECT_UPLOAD_LIMIT = 100 * 1024 * 1024;

export type UploadcareSettings = {
  urlBase?: string;
  multipartMinSize: number;
};

export function getUploadcareSettings({
  isNative,
  appOrigin,
  pageOrigin,
}: {
  isNative: boolean;
  appOrigin: string | undefined;
  pageOrigin: string;
}): UploadcareSettings {
  const multipartMinSize = UPLOADCARE_DIRECT_UPLOAD_LIMIT + 1;
  // A native WebView's origin is localhost, which has no proxy behind it —
  // without a configured app origin, upload to Uploadcare directly.
  const origin = appOrigin ?? (isNative ? undefined : pageOrigin);
  if (!origin) return { multipartMinSize };
  return { urlBase: `${origin}${UPLOADCARE_PROXY_PATH}`, multipartMinSize };
}

export class UploadcareUploadError extends Error {
  constructor(readonly reason: string) {
    super(`Uploadcare upload failed: ${reason}`);
    this.name = "UploadcareUploadError";
  }
}

type UploadcareFileInfo = {
  uuid: string;
  cdnUrl: string;
  name: string;
  mimeType: string;
};

function currentUploadcareSettings() {
  return getUploadcareSettings({
    isNative: Capacitor.isNativePlatform(),
    // Read from the runtime config directly rather than `getAppOrigin()`:
    // `environment.ts` reads `import.meta.env` at module load, which breaks
    // the many Jest suites that import this helper transitively.
    appOrigin: getRuntimeConfig().VITE_APP_ORIGIN,
    pageOrigin: window.location.origin,
  });
}

// The single entry point for Uploadcare uploads. On failure it notifies the
// user itself and rejects with an `UploadcareUploadError`, so callers only
// need to stop their flow — they must not show a second message for it.
export const uploadFileToUploadCare = async (
  file: File,
  needCompress?: boolean
) => {
  try {
    // Inside the `try`: a failed preprocessing step (e.g. a malformed HEIC)
    // is an upload failure for the caller too.
    const fileObject =
      needCompress === false || !canBeCompressed(file)
        ? file
        : await compress(file, IMAGE_QUALITY, IMAGE_MAX_WIDTH);
    // Larger files would take the widget's multipart flow, whose parts go
    // straight to *.s3.amazonaws.com and bypass the proxy.
    if (fileObject.size > UPLOADCARE_DIRECT_UPLOAD_LIMIT) {
      throw new UploadcareUploadError(
        `file is larger than the ${UPLOADCARE_DIRECT_UPLOAD_LIMIT} byte direct-upload limit`
      );
    }
    const upload = uploadcare.fileFrom(
      "object",
      fileObject,
      currentUploadcareSettings()
    );
    return await new Promise<Attachment>((resolve, reject) => {
      upload
        .done((fileInfo: UploadcareFileInfo) => {
          resolve({
            id: "volatile::" + fileInfo.uuid,
            url: fileInfo.cdnUrl,
            title: fileInfo.name,
            mimeType: fileInfo.mimeType,
          });
        })
        .fail((reason: unknown) => {
          reject(new UploadcareUploadError(String(reason)));
        });
    });
  } catch (error) {
    // Callers suppress `UploadcareUploadError` to avoid a second snackbar, so
    // report it here or it never reaches Sentry.
    Sentry.captureException(error);
    // Loaded lazily: the Tolgee instance reads `import.meta.env` at module
    // load, and this helper is imported by many components under Jest.
    const { tolgee } = await import("../../../common/translations/tolgee.js");
    enqueueSnackbar(
      tolgee.t("Upload failed. Please try again.", { ns: "Global" }),
      { variant: "error" }
    );
    // A synchronous throw from the widget gets the same type, so callers
    // recognise it as already notified.
    throw error instanceof UploadcareUploadError
      ? error
      : new UploadcareUploadError(String(error));
  }
};

interface UploadCareFileInfo {
  uuid: string;
  cdnUrl: string;
  name: string;
  mimeType: string;
}

// `uploadcare-widget` ships without types; this is the subset of its
// jQuery-deferred-style upload handle used below.
interface UploadCareUpload {
  done(callback: (info: UploadCareFileInfo) => void): UploadCareUpload;
  fail(callback: () => void): UploadCareUpload;
  cancel(): void;
}

/**
 * Imports a remote image into UploadCare by URL (UploadCare fetches it
 * server-side). Resolves `null` instead of rejecting when the import fails,
 * times out, or the stored file is not an image.
 */
export const importImageFromUrl = (
  url: string,
  { timeoutMs }: { timeoutMs: number }
): Promise<Attachment | null> =>
  new Promise(resolve => {
    let settled = false;
    const settle = (attachment: Attachment | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(attachment);
    };

    let upload: UploadCareUpload;
    try {
      upload = uploadcare.fileFrom("url", url, currentUploadcareSettings());
    } catch {
      resolve(null);
      return;
    }

    const timer = setTimeout(() => {
      upload.cancel();
      settle(null);
    }, timeoutMs);

    upload.done(info => {
      settle(
        info.mimeType.startsWith("image/")
          ? {
              id: "volatile::" + info.uuid,
              url: info.cdnUrl,
              title: info.name,
              mimeType: info.mimeType,
            }
          : null
      );
    });
    upload.fail(() => settle(null));
  });

export async function compressIfCan(file: File) {
  if (canBeCompressed(file)) {
    return await compress(file, IMAGE_QUALITY, IMAGE_MAX_WIDTH);
  }

  return file;
}

const NON_COMPRESSABLE_TYPES = [
  "image/png",
  "image/svg",
  "image/svg+xml",
  "image/webp",
];
export function canBeCompressed(file: File) {
  return !NON_COMPRESSABLE_TYPES.includes(file.type);
}

export function dataURLtoFile(dataurl: string, filename: string) {
  let arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)![1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export function getRotatedUrl(url: string, direction: "left" | "right") {
  if (url.includes("autorotate")) {
    return url.replace(
      "/autorotate/yes/",
      `/rotate/${direction !== "left" ? "270" : "90"}/`
    );
  }

  if (url.includes("/rotate/90/")) {
    return url.replace(
      "/rotate/90/",
      `/rotate/${direction !== "left" ? "0" : "180"}/`
    );
  }

  if (url.includes("/rotate/180/")) {
    return url.replace(
      "/rotate/180/",
      `/rotate/${direction !== "left" ? "90" : "270"}/`
    );
  }

  if (url.includes("/rotate/270/")) {
    return url.replace(
      "/rotate/270/",
      `/rotate/${direction !== "left" ? "180" : "0"}/`
    );
  }

  if (url.includes("/rotate/0/")) {
    return url.replace(
      "/rotate/0/",
      `/rotate/${direction !== "left" ? "270" : "90"}/`
    );
  }

  if (!url.includes("/rotate/") && !url.includes("/autorotate/")) {
    return `${url}-/rotate/${direction !== "left" ? "270" : "90"}/`;
  }

  return url;
}

async function compress(
  source: File,
  quality: number,
  maxWidth: number
): Promise<File> {
  if (!isPicture(source.type)) return source;

  let file = source;
  if (file.type === "image/heic" || file.type === "image/heif") {
    const { default: heic2any } = await import("heic2any");
    const newBlob = (await heic2any({
      blob: file,
    })) as Blob;
    file = new File([newBlob], file.name, {
      lastModified: file.lastModified,
    });
  }
  const { origWidth, orientation } = await new Promise<{
    origWidth: number;
    origHeight: number;
    orientation: number | undefined;
  }>((resolve, reject) => {
    const fr = new FileReader();
    fr.readAsDataURL(file);
    fr.onload = function () {
      // file is loaded
      const img = new Image();

      img.onload = function () {
        exifrOrientation(img).then(orientation => {
          resolve({
            origWidth: img.width,
            origHeight: img.height,
            orientation: orientation,
          });
        });
      };

      (img as any).src = fr.result; // is the data URL because called with readAsDataURL
    };
  });

  return new Promise((resolve, reject) => {
    const fileName = file.name;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = event => {
      const img = new Image();
      (img as any).src = (event.target! as any).result;
      img.onload = () => {
        const width = origWidth > maxWidth ? maxWidth : origWidth;
        const scaleFactor = width / img.width;
        const height = img.height * scaleFactor;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d")!;

        canvas.width = width;
        canvas.height = height;

        ctx.save();
        // https://gist.github.com/SagiMedina/f00a57de4e211456225d3114fd10b0d0
        // switch (orientation) {
        //   // explained here: https://i.stack.imgur.com/6cJTP.gif
        //   case 1:
        //     break;
        //
        //   case 2:
        //     ctx.translate(width, 0);
        //     ctx.scale(-1, 1);
        //     break;
        //
        //   case 3:
        //     ctx.translate(width, height);
        //     ctx.rotate((180 / 180) * Math.PI); // 180/180 is 1? No shit, but how else will you know its need 180 rotation?
        //     break;
        //
        //   case 4:
        //     ctx.translate(0, height);
        //     ctx.scale(1, -1);
        //     break;
        //
        //   case 5:
        //     canvas.width = height;
        //     canvas.height = width;
        //     ctx.rotate((90 / 180) * Math.PI);
        //     ctx.scale(1, -1);
        //     break;
        //
        //   case 6:
        //     canvas.width = height;
        //     canvas.height = width;
        //     ctx.rotate((90 / 180) * Math.PI);
        //     ctx.translate(0, -height);
        //     break;
        //
        //   case 7:
        //     canvas.width = height;
        //     canvas.height = width;
        //     ctx.rotate((270 / 180) * Math.PI);
        //     ctx.translate(-width, height);
        //     ctx.scale(1, -1);
        //     break;
        //
        //   case 8:
        //     canvas.width = height;
        //     canvas.height = width;
        //     ctx.translate(0, width);
        //     ctx.rotate((270 / 180) * Math.PI);
        //     break;
        //
        //   default:
        //     break;
        // }

        ctx.drawImage(img, 0, 0, width, height);
        ctx.restore();

        ctx.canvas.toBlob(
          blob => {
            resolve(
              new File([blob!], fileName, {
                type: "image/jpeg",
                lastModified: Date.now(),
              })
            );
          },
          "image/jpeg",
          quality
        );
      };
    };
    reader.onerror = error => {
      console.error(error);
      reject(error);
    };
  });
}
